import React, { FC } from 'react';
import styled from '@emotion/styled';

import { getBackgroundTheme, getTextColor } from '../../style';
import GalleryModal from '../../modules/modals/GalleryModal';
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll';
import FTWIcon from '../../atoms/icons/FTWIcon';
import { FONT_FAMILIES } from 'theme/material/constants';
import { isDarkMode } from 'utils/helpers';
import PrimaryButton from 'atoms/buttons/PrimaryButton';

const ModalHeader = styled('h3')(() => ({
  fontFamily: FONT_FAMILIES.secondary,
  fontWeight: 400,
  fontSize: 24,
  lineHeight: '18px',
  letterSpacing: -0.3,
  marginTop: 8,
  marginBottom: 20,
  color: isDarkMode() ? '#CFCFCF' : '#333333',
}));

const ModalDescription = styled('div')(() => ({
  marginTop: 32,
  ...getTextColor().DARK,
}));

const ModalTextWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const ModalText = styled('div')(() => ({
  fontSize: 15,
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: -0.3,
  color: isDarkMode() ? '#AFB0B1' : '#595959',
}));

const ButtonContainer = styled('div')({
  textAlign: 'center',
});

interface FirstnameInfoModalProps {
  onClose(): void;
  isOpen: boolean;
  alwaysDisabledBodyScroll?: boolean;
}

const FirstnameInfoModal: FC<FirstnameInfoModalProps> = ({ onClose, isOpen, alwaysDisabledBodyScroll = false }) => {
  useDisableBodyScroll(isOpen || alwaysDisabledBodyScroll);

  return (
    <GalleryModal
      onClose={onClose}
      customStyle={{
        overlay: {
          zIndex: 80000,
        },
        content: {
          backgroundColor: getBackgroundTheme().block2,
          padding: 30,
          maxWidth: 315,
        },
      }}
      contentLabel=""
      isOpen={isOpen}
      customCloseBtnStyle={{ height: 'max-content', width: '100%', textAlign: 'right', padding: '30px 30px 0 30px' }}
      customContentStyle={{ padding: 0, position: 'unset' }}
    >
      <ModalDescription data-test-id="authenticity-ftw">
        <FTWIcon />
        <ModalHeader>Authenticity FTW!</ModalHeader>

        <ModalTextWrapper>
          <ModalText>
            To create better and more personal connections, use a real first name or nickname. Be authentic and don’t
            use a handle such as “LovelyAngel” or “FunInTheSun,” etc.
          </ModalText>

          <ModalText>
            Only letters are allowed and you may change this only once. To change this in the future, please contact
            Customer Support.
          </ModalText>
        </ModalTextWrapper>
        <ButtonContainer>
          <PrimaryButton
            color="blue"
            size="small"
            data-test-id={`upload-another`}
            onClick={onClose}
            label="Got it"
            customStyle={{
              marginTop: 30,
              borderRadius: 4,
              fontWeight: 600,
              fontSize: 14,
              lineHeight: '16px',
              width: 'fit-content',
              padding: '12px 22px',
              height: 'auto',
            }}
          />
        </ButtonContainer>
      </ModalDescription>
    </GalleryModal>
  );
};

const MemoizedFirstnameInfoModal = React.memo(FirstnameInfoModal);

export default MemoizedFirstnameInfoModal;
